/**
 * Iter 67cs — Admin panel for managing platform referral partners (hotels,
 * agencies, concierges). CRUD over `/api/admin/partners`.
 *
 * Layout: top row = platform-default commission %. Below = sortable table
 * of partners with inline edit (commission %, active toggle), a "Reset
 * password" + "Deactivate" action menu, and a "+ New partner" dialog that
 * captures email/password/business profile in one step.
 *
 * Per the iter 67cs contract, partner commission is paid out of the platform
 * service fee, so the % field here represents a slice of the platform's own
 * cut — vendor revenue is never touched.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Save, KeyRound, Trash2, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const PARTNER_KINDS = [
  { value: "hotel",     label: "Hotel" },
  { value: "agency",    label: "Travel agency" },
  { value: "concierge", label: "Concierge" },
  { value: "other",     label: "Other" },
];

export default function AdminPartnersPanel() {
  const [defaultPct, setDefaultPct] = useState(0);
  const [defaultDraft, setDefaultDraft] = useState(0);
  const [savingDefault, setSavingDefault] = useState(false);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/partners/settings"),
      api.get("/admin/partners"),
    ]).then(([s, p]) => {
      setDefaultPct(Number(s.data.default_commission_percent || 0));
      setDefaultDraft(Number(s.data.default_commission_percent || 0));
      setPartners(p.data || []);
    }).catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const saveDefault = async () => {
    setSavingDefault(true);
    try {
      await api.put("/admin/partners/settings", { default_commission_percent: Number(defaultDraft) });
      toast.success(`Platform default set to ${defaultDraft}%`);
      setDefaultPct(Number(defaultDraft));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingDefault(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-partners-panel">
      <div className="rounded-2xl bg-gradient-to-br from-pink-50/40 to-white ring-1 ring-pink-100 p-5">
        <div className="text-[10px] uppercase tracking-wider font-bold text-pink-700 mb-2">Platform default</div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Default commission % of platform service fee</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={defaultDraft}
                onChange={(e) => setDefaultDraft(e.target.value)}
                className="max-w-[140px]"
                data-testid="admin-partners-default-input"
              />
              <span className="text-xs text-neutral-500">% — applied when a partner has no per-partner override</span>
            </div>
          </div>
          <Button onClick={saveDefault} disabled={savingDefault || Number(defaultDraft) === defaultPct} className="rounded-full" data-testid="admin-partners-default-save">
            <Save className="h-3.5 w-3.5 mr-1.5" /> Save default
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-xl font-semibold tracking-tight">Referral partners</h3>
          <p className="text-xs text-neutral-500">Hotels, agencies, and concierges that bring renters to the platform.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={refresh} variant="outline" size="sm" className="rounded-full" data-testid="admin-partners-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-pink-600 hover:bg-pink-700" data-testid="admin-partners-create">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> New partner
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>New referral partner</DialogTitle>
                <DialogDescription>
                  Creates a login + a unique referral code. Email + password are shared with the partner manually — they then sign in at /dashboard/partner to copy their link.
                </DialogDescription>
              </DialogHeader>
              <_CreateForm onCreated={() => { setCreateOpen(false); refresh(); }} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading partners…</div>
      ) : partners.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-12 text-center" data-testid="admin-partners-empty">
          <div className="text-sm font-semibold text-neutral-700 mb-1">No partners yet</div>
          <div className="text-xs text-neutral-500">Click "New partner" to onboard your first hotel or agency.</div>
        </div>
      ) : (
        <div className="rounded-2xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50/60 text-[10px] uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-bold">Business</th>
                  <th className="text-left px-4 py-2.5 font-bold">Code</th>
                  <th className="text-left px-4 py-2.5 font-bold">Country</th>
                  <th className="text-right px-4 py-2.5 font-bold">Commission %</th>
                  <th className="text-right px-4 py-2.5 font-bold">Bookings</th>
                  <th className="text-right px-4 py-2.5 font-bold">Commission €</th>
                  <th className="text-left px-4 py-2.5 font-bold">Status</th>
                  <th className="text-right px-4 py-2.5 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => (
                  <_Row key={p.id} partner={p} onChanged={refresh} platformDefault={defaultPct} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function _CreateForm({ onCreated }) {
  const [form, setForm] = useState({
    email: "", password: "", name: "", business_name: "",
    partner_kind: "hotel", country: "", referral_percent: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.referral_percent === "" || payload.referral_percent === null) {
        delete payload.referral_percent;
      } else {
        payload.referral_percent = Number(payload.referral_percent);
      }
      if (!payload.country) delete payload.country;
      await api.post("/admin/partners", payload);
      toast.success(`Partner created · share login: ${form.email}`);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  const canSubmit = form.email && form.password && form.name && form.business_name;
  return (
    <div className="space-y-3 mt-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Business name</Label>
          <Input value={form.business_name} onChange={set("business_name")} placeholder="Hotel Roma" data-testid="admin-partners-form-business" />
        </div>
        <div>
          <Label className="text-xs">Kind</Label>
          <Select value={form.partner_kind} onValueChange={set("partner_kind")}>
            <SelectTrigger data-testid="admin-partners-form-kind"><SelectValue /></SelectTrigger>
            <SelectContent>{PARTNER_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Contact name</Label>
          <Input value={form.name} onChange={set("name")} placeholder="Marco Rossi" data-testid="admin-partners-form-name" />
        </div>
        <div>
          <Label className="text-xs">Country (optional)</Label>
          <Input value={form.country} onChange={set("country")} placeholder="Italy" data-testid="admin-partners-form-country" />
        </div>
        <div>
          <Label className="text-xs">Login email</Label>
          <Input type="email" value={form.email} onChange={set("email")} placeholder="hotel@example.com" data-testid="admin-partners-form-email" />
        </div>
        <div>
          <Label className="text-xs">Password (≥8 chars)</Label>
          <Input value={form.password} onChange={set("password")} placeholder="Set a temporary password" data-testid="admin-partners-form-password" />
        </div>
        <div>
          <Label className="text-xs">Commission % (override — leave blank for default)</Label>
          <Input type="number" min={0} max={100} step={0.5} value={form.referral_percent} onChange={set("referral_percent")} placeholder="e.g. 10" data-testid="admin-partners-form-percent" />
        </div>
      </div>
      <Button onClick={submit} disabled={!canSubmit || saving} className="rounded-full bg-pink-600 hover:bg-pink-700" data-testid="admin-partners-form-submit">
        {saving ? "Creating…" : "Create partner"}
      </Button>
    </div>
  );
}

function _Row({ partner, onChanged, platformDefault }) {
  const [pctDraft, setPctDraft] = useState(partner.referral_percent ?? "");
  const [savingPct, setSavingPct] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [newPw, setNewPw] = useState("");

  const effective = partner.referral_percent ?? platformDefault;
  const refUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/cars?ref=${encodeURIComponent(partner.referral_code)}`;

  const savePct = async () => {
    setSavingPct(true);
    try {
      const v = pctDraft === "" ? null : Number(pctDraft);
      await api.put(`/admin/partners/${partner.id}`, { referral_percent: v });
      toast.success("Commission updated");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingPct(false);
    }
  };

  const toggleActive = async () => {
    try {
      await api.put(`/admin/partners/${partner.id}`, { is_active: !partner.is_active });
      toast.success(partner.is_active ? "Partner deactivated" : "Partner reactivated");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetPw = async () => {
    if (!newPw || newPw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      await api.put(`/admin/partners/${partner.id}/password`, { password: newPw });
      toast.success("Password reset. Share the new password with the partner.");
      setPwOpen(false);
      setNewPw("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <tr className="border-t border-border" data-testid={`admin-partner-row-${partner.id}`}>
      <td className="px-4 py-3">
        <div className="font-semibold text-neutral-900">{partner.business_name}</div>
        <div className="text-[11px] text-neutral-500">{partner.name} · {partner.email}</div>
      </td>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={() => { navigator.clipboard.writeText(refUrl); toast.success("Link copied"); }}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-pink-100 text-pink-900 ring-1 ring-pink-200 text-[11px] font-mono font-bold hover:bg-pink-200 transition"
          title="Copy referral link"
          data-testid={`admin-partner-copy-${partner.id}`}
        >
          {partner.referral_code} <Copy className="h-2.5 w-2.5" />
        </button>
      </td>
      <td className="px-4 py-3 text-xs text-neutral-700">{partner.country || "—"}</td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-1">
          <Input
            type="number"
            min={0}
            max={100}
            step={0.5}
            value={pctDraft}
            onChange={(e) => setPctDraft(e.target.value)}
            placeholder={String(platformDefault)}
            className="w-20 h-8 text-xs"
            data-testid={`admin-partner-pct-${partner.id}`}
          />
          <Button size="sm" onClick={savePct} disabled={savingPct || String(pctDraft) === String(partner.referral_percent ?? "")} className="h-8 px-2 rounded-full">
            <Save className="h-3 w-3" />
          </Button>
        </div>
        <div className="text-[10px] text-neutral-400 mt-0.5">effective {effective}%</div>
      </td>
      <td className="px-4 py-3 text-right tabular-nums">{partner.stats?.total_bookings || 0}</td>
      <td className="px-4 py-3 text-right tabular-nums font-semibold text-emerald-700">€{(partner.stats?.commission_total_eur || 0).toFixed(2)}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ring-1 text-[10px] font-bold uppercase tracking-wider ${
          partner.is_active ? "bg-emerald-100 text-emerald-800 ring-emerald-200" : "bg-neutral-100 text-neutral-500 ring-neutral-200"
        }`}>{partner.is_active ? "Active" : "Disabled"}</span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex items-center gap-1">
          <Dialog open={pwOpen} onOpenChange={setPwOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 rounded-full" data-testid={`admin-partner-pw-${partner.id}`}>
                <KeyRound className="h-3 w-3 mr-1" /> Password
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reset password — {partner.business_name}</DialogTitle>
                <DialogDescription>The partner won't be notified automatically. Share the new password with them manually.</DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label className="text-xs">New password (≥8 chars)</Label>
                <Input value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid={`admin-partner-pw-input-${partner.id}`} />
                <Button onClick={resetPw} className="rounded-full bg-pink-600 hover:bg-pink-700" data-testid={`admin-partner-pw-save-${partner.id}`}>Save</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button onClick={toggleActive} variant="outline" size="sm" className="h-8 rounded-full" data-testid={`admin-partner-toggle-${partner.id}`}>
            {partner.is_active ? <Trash2 className="h-3 w-3" /> : <RefreshCw className="h-3 w-3" />}
          </Button>
        </div>
      </td>
    </tr>
  );
}
